import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Link, useMediaQuery, useTheme, Alert, Stack } from '@mui/material';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { Menu as MenuIcon } from '@mui/icons-material';
import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { useEntityPage } from 'c-hooks';
import { useEntityData } from 'c-data';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import { CampaignReportingWrapper } from 'c-main/Components';
import type { LoadScriptProps } from '@react-google-maps/api';
import { LoadScript } from '@react-google-maps/api';
import { Translate } from 'c-translation';
import ReactGA from 'react-ga';

import { campaignIcon } from 'c-main/icons';
import type { SystemSearchEntityResource } from 'c-system-search';
import { IfHasAllPermissions } from 'c-auth-module/Components';

import PCADataComingSoon from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar/PCADataComingSoon';

import Sidebar from '../Campaign/CampaignOverviewPage/SideBar';

const mapLibraries: LoadScriptProps['libraries'] = ['visualization'];

// Inline banner row shown where the Card used to be
const WarningStrips: React.FC<{ campaign?: Campaign }> = ({ campaign }) => {
    const hasAwaitingData = useMemo(
        () => campaign?.notices?.overall?.awaiting_data || campaign?.data_status?.awaiting_data,
        [campaign],
    );
    const hasInspectingData = useMemo(
        () => campaign?.notices?.overall?.inspecting_data || campaign?.data_status?.inspecting_data,
        [campaign],
    );
    const needsBookingValidation = useMemo(
        () =>
            campaign?.booking_summary_validated === false ||
            campaign?.notices?.overall?.awaiting_booking_plan ||
            campaign?.data_status?.awaiting_booking_plan,
        [campaign],
    );

    // nothing to show — keep space tight
    if (!hasAwaitingData && !hasInspectingData && !needsBookingValidation) return null;

    return (
        <Stack
            direction="row"
            spacing={1.5}
            sx={{ width: '100%', mb: 2 }}
            useFlexGap
            flexWrap="wrap"
        >
            <IfHasAllPermissions permissions={[PermissionName.GeneralView_data_status]}>
                {hasAwaitingData && (
                    <Alert severity="warning" variant="outlined" sx={{ flex: 1, minWidth: 260 }}>
                        <Translate path="Modules.Main.Campaigns.DataStatus.awaitingDataLabelAlert" />
                    </Alert>
                )}
                {hasInspectingData && (
                    <Alert severity="warning" variant="outlined" sx={{ flex: 1, minWidth: 260 }}>
                        <Translate path="Modules.Main.Campaigns.DataStatus.inspectingDataLabelAlert" />
                    </Alert>
                )}
            </IfHasAllPermissions>

            <IfHasAllPermissions permissions={[PermissionName.GeneralView_data_status_booking]}>
                {needsBookingValidation && (
                    <Alert severity="warning" variant="outlined" sx={{ flex: 1, minWidth: 260 }}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Translate path="Modules.Main.Campaigns.DataStatus.bookingPlanValidatedLabelAlert" />
                            <Link
                                href="mailto:data@uniledsoftware.com"
                                target="_blank"
                                sx={{ fontWeight: 600 }}
                            >
                                data@uniledsoftware.com
                            </Link>
                        </Box>
                    </Alert>
                )}
            </IfHasAllPermissions>
        </Stack>
    );
};

const CampaignReportingPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);

    const { id } = useEntityPage();
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });
    const { hasPCAData } = useCampaignWrapperState(id);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const toggleMobileDrawer = useCallback(() => setMobileDrawerOpen(p => !p), []);
    const isDspCampaign = useMemo(() => {
        if (!campaign) return false;

        if (id === 8896) return true;

        if (campaign.type === 'linear' || campaign.type === 'default') {
            return id === 9464 || id === 10160;
        }
        return true;
    }, [campaign, id]);
    return (
        <LoadScript
            googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY}
            libraries={mapLibraries}
        >
            {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            borderBottom: '1px solid',
                            borderColor: 'grey.300',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: 'background.paper',
                        }}
                    >
                        <IconButton onClick={toggleMobileDrawer} edge="start" aria-label="menu">
                            <MenuIcon />
                        </IconButton>
                    </Box>
                    <Sidebar
                        id={id}
                        mobileDrawerOpen={mobileDrawerOpen}
                        toggleMobileDrawer={toggleMobileDrawer}
                    />
                    <Box sx={{ flexGrow: 1, px: 2, pt: 2 }}>
                        <WarningStrips campaign={campaign} />
                        {!hasPCAData ? (
                            <PCADataComingSoon campaign={campaign} />
                        ) : (
                            <CampaignReportingWrapper id={id} campaign={campaign} />
                        )}
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex' }}>
                    <Sidebar id={id} />
                    <Box component="main" sx={{ flexGrow: 1, minWidth: 0, pt: 2, px: 2 }}>
                        <WarningStrips campaign={campaign} />
                        {!hasPCAData ? (
                            <PCADataComingSoon campaign={campaign} />
                        ) : (
                            <CampaignReportingWrapper id={id} campaign={campaign} />
                        )}
                    </Box>
                </Box>
            )}
        </LoadScript>
    );
};

const PageBreadcrumbs: SystemSearchEntityResource<Campaign>['renderEntityBreadcrumbs'] = () => (
    <Box display="flex" />
);

const setup: PageEntityConfig<Campaign> = {
    id: 'CampaignReportingPage',
    component: CampaignReportingPage,
    systemSearch: {
        title: undefined,
        description: 'Modules.Main.Campaigns.Overview.description',
        icon: campaignIcon,
        type: 'entity',
        aliases: undefined,
        route: PostAuthRoutes.Campaigns.CampaignReporting,
        saveAsRecentPage: true,
        entityName: 'Campaign',
        prefix: 'SystemSearch.prefixes.campaign',
        prefixDescription: 'SystemSearch.prefixDescriptions.campaign',
        breadcrumbs: [
            {
                label: 'Modules.Main.Campaigns.List.metaTitle',
                link: PostAuthRoutes.Campaigns.CampaignList,
            },
        ],
        nameGen: entity => `${entity?.name}-${entity?.id}`,
        defaultIncludes: [
            'agency',
            'market.currency',
            'creatives',
            'client',
            'creatives.resolution',
            'creatives.file',
            'budgetSummary',
            'budgetSummary.lineItems',
            'budgetSummaries',
            'budgetSummaries.lineItems',
            'notices',
            'errors',
            'creativeThumbnails',
            'bookingSummaries',
            'popShotsCount',
            'missingDisplaysCount',
            'happydemics',
            'additionalContacts',
            'campaignOrderlineSitelistErrors',
        ],
        permissions: [PermissionName.UniledportalCampaignRead],
        renderEntityBreadcrumbs: PageBreadcrumbs,
    },
};

export default setup;
